import React, { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import LoadingFallback from './components/LoadingFallback';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';

// Layouts (keep static - critical for app structure)
import AdminLayout from './components/layout/AdminLayout';
import ClientLayout from './components/layout/ClientLayout';

// Auth Pages (keep static - critical for initial load)
import Login from './pages/Login';
import PasswordSetupPage from './pages/PasswordSetupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Admin Pages - Lazy Loaded
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const ClientsPage = lazy(() => import('./pages/admin/Clients'));
const ClientEmailLists = lazy(() => import('./pages/admin/ClientEmailLists'));
const ClientDetail = lazy(() => import('./pages/admin/ClientDetail'));
const ClientsTrash = lazy(() => import('./pages/admin/ClientsTrash'));
const AdminServices = lazy(() => import('./pages/admin/Services'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminInvoices = lazy(() => import('./pages/admin/Invoices'));
const AdminSupport = lazy(() => import('./pages/admin/Tickets'));
const AdminDomains = lazy(() => import('./pages/admin/Domains'));
const ProjectsPage = lazy(() => import('./pages/admin/Projects'));
const KanbanPage = lazy(() => import('./pages/admin/Kanban'));
const CRMPage = lazy(() => import('./pages/admin/CRM'));
const LeadDetail = lazy(() => import('./pages/admin/LeadDetail'));
const SettingsPage = lazy(() => import('./pages/admin/Settings'));
const DeveloperSettings = lazy(() => import('./pages/admin/DeveloperSettings'));
const ServersPage = lazy(() => import('./pages/admin/Servers'));
const GeneralSettings = lazy(() => import('./pages/admin/GeneralSettings'));
const QuoteBuilderPage = lazy(() => import('./pages/admin/QuoteBuilderPage'));
const CategoriesPage = lazy(() => import('./pages/admin/Categories'));
const IntegrationsDashboard = lazy(() => import('./pages/admin/IntegrationsDashboard'));
const SpaceshipIntegration = lazy(() => import('./pages/admin/SpaceshipIntegration'));
const DomainRegistrars = lazy(() => import('./pages/admin/DomainRegistrars'));
const DomainBilling = lazy(() => import('./pages/admin/DomainBilling'));
const EmailTemplates = lazy(() => import('./pages/admin/EmailTemplates'));
const StorageDashboard = lazy(() => import('./pages/admin/StorageDashboard'));
const CommunicationsDashboard = lazy(() => import('./pages/admin/CommunicationsDashboard'));
const NewslettersPage = lazy(() => import('./pages/admin/NewslettersPage'));
const NewsletterEditorPage = lazy(() => import('./pages/admin/NewsletterEditorPage'));
const NewsletterAnalyticsPage = lazy(() => import('./pages/admin/NewsletterAnalyticsPage').then(m => ({ default: m.NewsletterAnalyticsPage })));
const NewsletterAnalyticsDetailPage = lazy(() => import('./pages/admin/NewsletterAnalyticsPage').then(m => ({ default: m.NewsletterAnalyticsDetailPage })));
const BlogListPage = lazy(() => import('./pages/admin/BlogListPage'));
const BlogEditorPage = lazy(() => import('./pages/admin/BlogEditorPage'));
const ActivityLogPage = lazy(() => import('./pages/admin/ActivityLogPage'));
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'));
const ApiKeysPage = lazy(() => import('./pages/admin/ApiKeys'));
const PhoneNumbersPage = lazy(() => import('./pages/admin/PhoneNumbers'));
const WebsiteMonitorPage = lazy(() => import('./pages/admin/WebsiteMonitor'));
const EchoSystemSettings = lazy(() => import('./pages/admin/EchoSystemSettings'));
const AdminProfilePage = lazy(() => import('./pages/admin/AdminProfilePage'));
const InvoicePrintPage = lazy(() => import('./pages/admin/InvoicePrintPage'));
const WHMCSImportPage = lazy(() => import('./pages/admin/WHMCSImportPage'));
const MailOversightPage = lazy(() => import('./pages/admin/MailOversight'));
const BlogListPublic = lazy(() => import('./pages/public/BlogPages').then(m => ({ default: m.BlogListPublic })));
const BlogPostPublic = lazy(() => import('./pages/public/BlogPages').then(m => ({ default: m.BlogPostPublic })));
const GatewayLog = lazy(() => import('./pages/admin/GatewayLog'));
const AutomationStatus = lazy(() => import('./pages/admin/AutomationStatus'));
const ProjectDetail = lazy(() => import('./pages/admin/ProjectDetail'));
const Transactions = lazy(() => import('./pages/admin/Transactions'));
const BarterContractsPage = lazy(() => import('./pages/admin/BarterContractsPage'));
const ContractDocumentsPage = lazy(() => import('./pages/admin/ContractDocumentsPage'));
const AdminManageUsers = lazy(() => import('./pages/admin/AdminManageUsers'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const SupportDashboard = lazy(() => import('./pages/admin/SupportDashboard'));

// Client Pages - Lazy Loaded
const ClientDashboard = lazy(() => import('./pages/client/Dashboard'));
const ClientServices = lazy(() => import('./pages/client/Services'));
const ClientDomains = lazy(() => import('./pages/client/Domains'));
const ClientInvoices = lazy(() => import('./pages/client/Invoices'));
const ClientSupport = lazy(() => import('./pages/client/Support'));
const ClientBilling = lazy(() => import('./pages/client/Billing'));
const ClientProfile = lazy(() => import('./pages/client/Profile'));
const ClientQuotes = lazy(() => import('./pages/client/Quotes'));
const ClientFiles = lazy(() => import('./pages/client/Files'));
const FileDrop = lazy(() => import('./pages/public/FileDrop'));
const ClientSiteBackups = lazy(() => import('./pages/client/SiteBackups'));
const ClientResellerMail = lazy(() => import('./pages/client/ResellerMail'));
const ClientSearchPerformance = lazy(() => import('./pages/client/SearchPerformance'));
const ClientProjects = lazy(() => import('./pages/client/Projects'));
const ClientManageUsers = lazy(() => import('./pages/client/ManageUsers'));
const ClientBusinesses = lazy(() => import('./pages/client/Businesses'));
const ClientBusinessDashboard = lazy(() => import('./pages/client/BusinessDashboard'));

// Dev Pages (Superuser Only) - Lazy Loaded
const DevLayout = lazy(() => import('./components/layout/DevLayout'));
const FormBuilder = lazy(() => import('./pages/dev/FormBuilder'));
const AiRules = lazy(() => import('./pages/dev/AiRules'));
const DevDatabase = lazy(() => import('./pages/dev/Database'));
const LocationGenerator = lazy(() => import('./components/LocationGenerator'));
const DevPageGeneratorV2 = lazy(() => import('./pages/DevPageGenerator'));
const PageGeneratorEditor = lazy(() => import('./pages/PageGeneratorEditor'));
const SeoAuditPage = lazy(() => import('./pages/SeoAuditPage'));
const CoverageAreaPage = lazy(() => import('./pages/CoverageAreaPage'));

// Public Pages - Lazy Loaded
const PublicForm = lazy(() => import('./pages/PublicForm'));
const QuoteSigningPage = lazy(() => import('./pages/QuoteSigningPage'));
const BarterSigningPage = lazy(() => import('./pages/BarterSigningPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const HomePage = lazy(() => import('./pages/public/HomePage'));
const DomainSearchPage = lazy(() => import('./pages/public/DomainSearchPage'));
const BasicPublicPage = lazy(() => import('./pages/public/BasicPublicPage'));
const WebDesignPage = lazy(() => import('./pages/public/WebDesignPage'));
const SeoExpertsPage = lazy(() => import('./pages/public/SeoExpertsPage'));
const SoftwareDevelopmentPage = lazy(() => import('./pages/public/SoftwareDevelopmentPage'));
const LocalSeoPage = lazy(() => import('./pages/public/LocalSeoPage'));
const SeoPricingPage = lazy(() => import('./pages/public/SeoPricingPage'));
const WebHostingPage = lazy(() => import('./pages/public/WebHostingPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const TvStreamPage = lazy(() => import('./pages/public/TvStreamPage'));
const ServicesPage = lazy(() => import('./pages/public/ServicesPage'));
const VoipServicesPage = lazy(() => import('./pages/public/VoipServicesPage'));
const PrintDesignPage = lazy(() => import('./pages/public/PrintDesignPage'));
const FullColorPrintingPage = lazy(() => import('./pages/public/FullColorPrintingPage'));
const ResellerProgramPage = lazy(() => import('./pages/public/ResellerProgramPage'));
const ClientDashboardPublicPage = lazy(() => import('./pages/public/ClientDashboardPublicPage'));
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const LocationDetailPage = lazy(() => import('./pages/LocationDetailPage'));
const InboxPage = lazy(() => import('./pages/admin/InboxPage'));
const BusinessDashboard = lazy(() => import('./pages/admin/BusinessDashboard'));
const LegacyForwarder = lazy(() => import('./pages/public/LegacyForwarder'));
import { LEGACY_ROUTE_REDIRECTS } from './data/publicRoutes';

// Protected Route Component
const STAFF_ROLES = ['superuser', 'admin', 'sales', 'support_lvl_2', 'support_lvl_1'];

const ProtectedRoute = ({ children, requiredRole, requireSuperuser = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 dark:border-orange-500" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Superuser-only routes
  if (requireSuperuser && user.role !== 'superuser') {
    return <Navigate to="/admin" replace />;
  }

  // Superusers can access everything
  if (user.role === 'superuser') {
    return children;
  }

  // "admin" required role means any staff role can access
  if (requiredRole === 'admin') {
    if (!STAFF_ROLES.includes(user.role)) {
      return <Navigate to="/client" replace />;
    }
    return children;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={STAFF_ROLES.includes(user.role) ? '/admin' : '/client'} replace />;
  }

  return children;
};

// Chat widget — only on public + client pages, never on admin/dev
const ChatWidgetLoader = () => {
  const { pathname } = useLocation();
  const isAdminOrDev = pathname.startsWith('/admin') || pathname.startsWith('/dev');

  useEffect(() => {
    // Hide chat widget on admin/dev pages
    if (isAdminOrDev) {
      const el = document.getElementById('a2g-chat-widget') || document.querySelector('[id*="a2g-chat"]');
      if (el) el.style.display = 'none';
      // Also hide common chat widget containers
      document.querySelectorAll('.a2g-chat-widget, #a2g-chat-container, [class*="chat-widget"]').forEach(e => e.style.display = 'none');
      return;
    }
    // Show it back on non-admin pages
    const el = document.getElementById('a2g-chat-widget') || document.querySelector('[id*="a2g-chat"]');
    if (el) el.style.display = '';
    document.querySelectorAll('.a2g-chat-widget, #a2g-chat-container, [class*="chat-widget"]').forEach(e => e.style.display = '');

    if (window.__a2gchat) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/a2g-chat/widget.js`)
      .then(r => r.text())
      .then(code => {
        // eslint-disable-next-line no-new-func
        new Function(code)();
      })
      .catch(() => {});
  }, [isAdminOrDev]);

  return null;
};

// App Routes Component
const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <>
      <ChatWidgetLoader />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/our-services" element={<ServicesPage />} />
      <Route path="/contact-us" element={<ContactPage />} />
      <Route path="/client-dashboard" element={<ClientDashboardPublicPage />} />
      <Route path="/privacy-policy" element={<BasicPublicPage />} />
      <Route path="/terms" element={<BasicPublicPage />} />
      <Route path="/search" element={<DomainSearchPage />} />
      <Route path="/page-suspended" element={<BasicPublicPage />} />
      <Route path="/server-landing-page" element={<BasicPublicPage />} />
      <Route path="/sitemap" element={<BasicPublicPage />} />
      <Route path="/streaming-ads-set-up" element={<BasicPublicPage />} />
      <Route path="/our-services/design/web-design-in-tampa" element={<WebDesignPage />} />
      <Route path="/our-services/design/software-development" element={<SoftwareDevelopmentPage />} />
      <Route path="/our-services/seo/seo-experts-around-the-web" element={<SeoExpertsPage />} />
      <Route path="/our-services/seo/local-seo" element={<LocalSeoPage />} />
      <Route path="/our-services/seo/seo-pricelist" element={<SeoPricingPage />} />
      <Route path="/our-services/hosting/web-hosting" element={<WebHostingPage />} />
      <Route path="/our-services/marketing/tv-stream" element={<TvStreamPage />} />
      <Route path="/our-services/voip/toll-free-services" element={<VoipServicesPage />} />
      <Route path="/our-services/design/print-design" element={<PrintDesignPage />} />
      <Route path="/our-services/printing/full-color-printing" element={<FullColorPrintingPage />} />
      <Route path="/our-services/reseller/reseller-programs" element={<ResellerProgramPage />} />
      <Route path="/our-services/seo/local-seo" element={<BasicPublicPage />} />
      <Route path="/our-services/seo/seo-pricing" element={<BasicPublicPage />} />
      <Route path="/our-services/hosting/a2g-hosting-package" element={<BasicPublicPage />} />
      <Route path="/our-services/marketing/streaming-advertising" element={<BasicPublicPage />} />
      <Route path="/our-business-services" element={<BasicPublicPage />} />
      <Route path="/our-business-services/web-design-services-in-tampa" element={<BasicPublicPage />} />
      <Route path="/our-business-services/local-toll-free-services" element={<BasicPublicPage />} />
      <Route path="/our-business-services/full-color-printing-in-tampa" element={<BasicPublicPage />} />
      <Route path="/our-business-services/seo-experts-in-tampa" element={<BasicPublicPage />} />
      <Route path="/our-business-services/seo-pricing" element={<BasicPublicPage />} />
      <Route path="/our-business-services/hosting-reseller" element={<BasicPublicPage />} />
      <Route path="/our-business-services/accessibility" element={<BasicPublicPage />} />
      <Route path="/our-business-services/design-services-in-tampa" element={<BasicPublicPage />} />
      <Route path="/our-business-services/full-color-printing-in-tampa/business-cards" element={<BasicPublicPage />} />
      <Route path="/our-business-services/full-color-printing-in-tampa/postcards" element={<BasicPublicPage />} />
      <Route path="/our-business-services/full-color-printing-in-tampa/bookmarks" element={<BasicPublicPage />} />
      <Route path="/component/content/article/:slug" element={<BasicPublicPage />} />
      {LEGACY_ROUTE_REDIRECTS.map((redirect) => (
        <Route key={redirect.from} path={redirect.from} element={<LegacyForwarder to={redirect.to} />} />
      ))}
      <Route path="/login" element={<Login />} />
      <Route path="/setup-password" element={<PasswordSetupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/form/:formId" element={<PublicForm />} />
      <Route path="/drop/:token" element={<FileDrop />} />
      <Route path="/sign/:quoteId" element={<QuoteSigningPage />} />
      <Route path="/barter/sign/:contractId" element={<BarterSigningPage />} />

      {/* Public Service Areas / Locations */}
      <Route path="/locations" element={<LocationsPage />} />

      {/* Dev Routes (Superuser Only) */}
      <Route
        path="/dev/ai-rules"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><AiRules /></DevLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dev/forms"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><FormBuilder /></DevLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dev/location-generator"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><LocationGenerator /></DevLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dev-settings/page-generator"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><DevPageGeneratorV2 /></DevLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dev-settings/page-generator/new"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><PageGeneratorEditor /></DevLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dev-settings/page-generator/:id"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><PageGeneratorEditor /></DevLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dev-settings/seo-audit"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><SeoAuditPage /></DevLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dev/database"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout><DevDatabase /></DevLayout>
          </ProtectedRoute>
        }
      />
      {/* Coverage Area public pages */}
      <Route path="/coverage-areas" element={<CoverageAreaPage />} />
      <Route path="/coverage-areas/:product_slug" element={<CoverageAreaPage />} />
      <Route path="/coverage-areas/:product_slug/:page_slug" element={<CoverageAreaPage />} />
      <Route
        path="/dev/*"
        element={
          <ProtectedRoute requireSuperuser>
            <DevLayout>
              <div className="text-white text-center py-12">
                <h2 className="text-xl font-bold mb-2">Coming Soon</h2>
                <p className="text-slate-400">This feature is under development</p>
              </div>
            </DevLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminDashboard /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ClientsPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/trash"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ClientsTrash /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:clientId"
        element={
          <ProtectedRoute requiredRole="admin">
            <ClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:clientId/businesses/:businessId"
        element={
          <ProtectedRoute requiredRole="admin">
            <BusinessDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:clientId/manage-users"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminManageUsers /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/services"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminServices /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminProducts /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminInvoices /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminSupport /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/domains"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminDomains /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/domain-billing"
        element={
          <ProtectedRoute requiredRole="admin">
            <DomainBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inbox"
        element={
          <ProtectedRoute requiredRole="admin">
            <InboxPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/newsletters"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><NewslettersPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/newsletters/analytics"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><NewsletterAnalyticsPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/newsletters/analytics/:newsletterId"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><NewsletterAnalyticsDetailPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/newsletters/new"
        element={
          <ProtectedRoute requiredRole="admin">
            <NewsletterEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/newsletters/:newsletterId"
        element={
          <ProtectedRoute requiredRole="admin">
            <NewsletterEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blog"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><BlogListPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blog/new"
        element={
          <ProtectedRoute requiredRole="admin">
            <BlogEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blog/:postId"
        element={
          <ProtectedRoute requiredRole="admin">
            <BlogEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminProfilePage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/whmcs-import"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><WHMCSImportPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/mail-oversight"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><MailOversightPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={<ProtectedRoute requiredRole="admin"><AdminLayout><ReportsPage /></AdminLayout></ProtectedRoute>}
      />
      <Route
        path="/admin/api-keys"
        element={<ProtectedRoute requiredRole="admin"><AdminLayout><ApiKeysPage /></AdminLayout></ProtectedRoute>}
      />
      <Route
        path="/admin/phone-numbers"
        element={<ProtectedRoute requiredRole="admin"><AdminLayout><PhoneNumbersPage /></AdminLayout></ProtectedRoute>}
      />
      <Route
        path="/admin/website-monitor"
        element={<ProtectedRoute requiredRole="admin"><AdminLayout><WebsiteMonitorPage /></AdminLayout></ProtectedRoute>}
      />
      <Route
        path="/admin/settings/echo-system"
        element={<ProtectedRoute requiredRole="admin"><AdminLayout><EchoSystemSettings /></AdminLayout></ProtectedRoute>}
      />
      <Route
        path="/admin/activity"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ActivityLogPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices/:invoiceId/print"
        element={
          <ProtectedRoute requiredRole="admin">
            <InvoicePrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/invoices/:invoiceId/print"
        element={
          <ProtectedRoute>
            <InvoicePrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ProjectsPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:projectId"
        element={
          <ProtectedRoute requiredRole="admin">
            <ProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kanban"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><KanbanPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/crm"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><CRMPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/crm/:leadId"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><LeadDetail /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/crm/:leadId/quote/new"
        element={
          <ProtectedRoute requiredRole="admin">
            <QuoteBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/crm/:leadId/quote/:quoteId"
        element={
          <ProtectedRoute requiredRole="admin">
            <QuoteBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><DeveloperSettings /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/general"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><GeneralSettings /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/integrations"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><IntegrationsDashboard /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/integrations/spaceship"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><SpaceshipIntegration /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/integrations/registrars"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><DomainRegistrars /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/servers"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ServersPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/email-templates"
        element={
          <ProtectedRoute requiredRole="admin">
            <EmailTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/client-email-lists"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ClientEmailLists /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/contracts"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><ContractDocumentsPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/storage"
        element={
          <ProtectedRoute requiredRole="admin">
            <StorageDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/notifications"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><CommunicationsDashboard /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/admin-users"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><AdminUsersPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support-dashboard"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><SupportDashboard /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing/gateway-log"
        element={
          <ProtectedRoute requiredRole="admin">
            <GatewayLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/utilities/automation"
        element={
          <ProtectedRoute requiredRole="admin">
            <AutomationStatus />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing/transactions"
        element={
          <ProtectedRoute requiredRole="admin">
            <Transactions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/barter"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><BarterContractsPage /></AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout><CategoriesPage /></AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* Client Routes */}
      <Route
        path="/client"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientDashboard /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/services"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientServices /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/domains"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientDomains /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/invoices"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientInvoices /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/support"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientSupport /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/billing"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientBilling /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/quotes"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientQuotes /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/profile"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientProfile /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/files"
        element={
          <ProtectedRoute>
            <ClientLayout><ClientFiles /></ClientLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/site-backups"
        element={<ProtectedRoute><ClientLayout><ClientSiteBackups /></ClientLayout></ProtectedRoute>}
      />
      <Route
        path="/client/search"
        element={<ProtectedRoute><ClientLayout><ClientSearchPerformance /></ClientLayout></ProtectedRoute>}
      />
      <Route
        path="/client/reseller-mail"
        element={<ProtectedRoute><ClientLayout><ClientResellerMail /></ClientLayout></ProtectedRoute>}
      />
      <Route
        path="/client/projects"
        element={<ProtectedRoute><ClientLayout><ClientProjects /></ClientLayout></ProtectedRoute>}
      />
      <Route
        path="/client/manage-users"
        element={<ProtectedRoute><ClientLayout><ClientManageUsers /></ClientLayout></ProtectedRoute>}
      />
      <Route
        path="/client/businesses"
        element={<ProtectedRoute><ClientLayout><ClientBusinesses /></ClientLayout></ProtectedRoute>}
      />
      <Route
        path="/client/businesses/:businessId"
        element={<ProtectedRoute><ClientLayout><ClientBusinessDashboard /></ClientLayout></ProtectedRoute>}
      />

      {/* Public Blog */}
      <Route path="/blog" element={<BlogListPublic />} />
      <Route path="/blog/:slug" element={<BlogPostPublic />} />

      {/* Catch All */}
      <Route path="*" element={<BasicPublicPage />} />
      </Routes>
    </Suspense>
    </>
  );
};

function App() {
  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').catch(() => {});
    }
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster />
            <PWAInstallPrompt />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
